"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { Handshake, Bot, Store, ArrowRight } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { productStats } from "@/lib/constants";

const steps = [
  {
    number: "01",
    title: "Expand",
    product: "Brand Partnerships",
    description:
      `Source products from ${productStats.brandPartnerships.brands} Shopify brands. Add partner dropshipping, wholesale, and marketplace listings to your store.`,
    href: "/brand-partnerships",
    icon: Handshake,
    color: "primary" as const,
  },
  {
    number: "02",
    title: "Convert",
    product: "Buddy AI",
    description:
      "Answer questions, recommend products, and close deals 24/7 with AI sales, support, and conversion.",
    href: "/buddy-ai-assistant",
    icon: Bot,
    color: "secondary" as const,
  },
  {
    number: "03",
    title: "Sell Everywhere",
    product: "Marketplace",
    description:
      "List your products on the Buddify Marketplace and reach new customers actively searching for Shopify brands.",
    href: "/marketplace",
    icon: Store,
    color: "tertiary" as const,
  },
];

const colorMap = {
  primary: {
    badge: "bg-primary-100 text-primary-700",
    icon: "bg-primary-100 text-primary-600",
    border: "border-primary-200",
    hover: "hover:border-primary-300 hover:bg-primary-50/30",
  },
  secondary: {
    badge: "bg-secondary-100 text-secondary-700",
    icon: "bg-secondary-100 text-secondary-600",
    border: "border-secondary-200",
    hover: "hover:border-secondary-300 hover:bg-secondary-50/30",
  },
  tertiary: {
    badge: "bg-sky-100 text-sky-700",
    icon: "bg-sky-100 text-sky-600",
    border: "border-sky-200",
    hover: "hover:border-sky-300 hover:bg-sky-50/30",
  },
};

export function EcosystemSection() {
  return (
    <Section background="gray" size="sm">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-micro-uppercase text-stone-500 mb-3">ECOSYSTEM</p>
          <h2 className="text-display-1 text-stone-900 mb-4">
            One platform. Three growth loops.
          </h2>
          <p className="text-body-lg text-stone-600 max-w-2xl mx-auto">
            Each product feeds the others. More supply means more conversions.
            More conversions mean more marketplace demand.
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          {/* Connector line - desktop */}
          <div className="hidden lg:block absolute top-24 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-primary-200 via-secondary-200 to-sky-200" />

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => {
              const colors = colorMap[step.color];
              const Icon = step.icon;

              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <Link
                    href={step.href}
                    className={`group relative block h-full rounded-2xl border ${colors.border} ${colors.hover} bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
                  >
                    {/* Number badge */}
                    <span
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${colors.badge} font-bold text-sm mb-6`}
                    >
                      {step.number}
                    </span>

                    {/* Icon */}
                    <div
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${colors.icon} mb-4 transition-transform duration-300 group-hover:scale-110`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <h3 className="text-h3 text-stone-900 mb-1">{step.title}</h3>
                    <p className={`text-sm font-semibold mb-3 ${colors.badge.split(" ")[1]}`}>
                      {step.product}
                    </p>
                    <p className="text-body-sm text-stone-600 mb-6">
                      {step.description}
                    </p>

                    {/* Link */}
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900 group-hover:text-primary-600 transition-colors">
                      Learn more
                      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Container>
    </Section>
  );
}
